<template>
  <div>
    <v-form ref="loginForm" @submit.prevent="">
      <!-- email -->
      <v-row v-if="!hideLoginForm">
        <v-col class="col-12 py-0">
          <v-text-field
            ref="mail"
            v-model="mail"
            validate-on-blur
            :rules="$store.getters.getMailRules()"
            :label="$t('general.email')"
            required
            :error-messages="mailErrorText"
            outlined
          />
        </v-col>

        <!-- password -->
        <v-col class="col-12 py-0">
          <v-text-field
            ref="password"
            v-model="password"
            v-focus
            validate-on-blur
            :rules="[(v) => !!v || this.$t('login.passwordRequired')]"
            :label="$t('general.password')"
            :append-icon="showPassword ? 'visibility_off' : 'visibility'"
            :type="showPassword ? 'text' : 'password'"
            required
            outlined
            @click:append="showPassword = !showPassword"
            @keyup.enter="login()"
          />
        </v-col>
      </v-row>

      <!-- save login authorization in cookie -->
      <v-row>
        <v-col class="col-12 py-0">
          <v-checkbox
            v-model="saveCookie"
            :label="$t('login.saveCookie')"
            color="primary"
            class="mt-0"
          />
        </v-col>
      </v-row>

      <!-- links -->
      <v-row class="text-left">
        <!-- no account yet -->
        <v-col
          v-if="
            !hideNoAccountHint &&
            !$store.getters.getAppDestinationInstance().isDefaultDestination()
          "
          class="col-12 py-0"
        >
          <a
            href=""
            @click.prevent.stop="$router.push({ name: 'registration' })"
          >
            {{ $t('loginForm.noAccount') }}
          </a>
        </v-col>

        <!-- forgotten login credentials -->
        <v-col
          v-if="
            !$store.getters.getAppDestinationInstance().isDefaultDestination()
          "
          class="col-12 py-0"
        >
          <a href="" @click.prevent.stop="$router.push({ name: 'resetPw' })">
            {{ $t('login.forgetPw') }}
          </a>
        </v-col>
      </v-row>

      <!-- login button -->
      <v-row class="mt-4">
        <v-col class="col-12 text-center py-0">
          <v-btn large outlined color="primary" @click="login()">
            {{ $t('loginForm.loginNow') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>

    <!-- if user exists and has no mail -->
    <s-modal
      ref="mailHintModal"
      hide-cancel-button
      :ok-text="$t('general.close')"
      :size="2"
      :headerText="$t('notify.passwordWasSent')"
      @close="hideLoginForm = false"
    >
      <v-row class="content-3 align-center">
        <v-col class="col-2 col-md-1">
          <span class="fal fa-envelope mr-2 green--text fa-2x" />
        </v-col>
        <v-col class="col-10 col-md-11">
          {{ $t('login.passwordSentExplanation') }}
        </v-col>
        <!-- more infos -->
        <v-col class="col-2 col-md-1">
          <span class="fal fa-info-circle blue--text fa-2x" />
        </v-col>
        <v-col class="col-10 col-md-11 content-3">
          {{ $t('login.passwordSentInfo') }}
        </v-col>
      </v-row>
    </s-modal>
  </div>
</template>

<script>
import Authentication from '../../classes-shared/authentication/Authentication'
import { shopInstance } from '../../classes-shared/utils/axiosInstance'
import cookies from 'js-cookie'

export default {
  name: 'LoginForm',

  props: {
    // for booking process, where no routing should happen
    noRouting: {
      type: Boolean,
      required: false,
      default: false,
    },

    // for booking process: hide no account link
    hideNoAccountHint: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      mail: '',
      password: '',
      showPassword: false,
      saveCookie: false,
      mailErrorText: null,
      hideLoginForm: false,
    }
  },

  computed: {
    // shortcut
    appDestinationInstance() {
      return this.$store.getters.getAppDestinationInstance()
    },

    // shortcut
    appUserInstance() {
      return this.$store.getters.getAppUserInstance()
    },
  },

  mounted() {
    if (this.$route.params.hasOwnProperty('performAutoLogin')) {
      // after registration
      this.performAutoLogin()
    } else if (this.$store.state.authorization === 0) {
      this.performAutoLogin()
    }

    // show password directly if coming from registration form with existing mail
    if (this.$route.params.hasOwnProperty('showMail')) {
      this.mail = this.$route.params.mail

      if (this.$route.params.hasOwnProperty('mailHintModal')) {
        this.hideLoginForm = true
        this.$refs.mailHintModal.show()
      }
    }

    /* global EventBus */
    EventBus.$on('notification:ok', () => {
      // focus password
      this.$nextTick(() => {
        this.$refs.password.focus()
      })
    })
  },

  beforeDestroy() {
    EventBus.$off('notification:ok')
  },

  methods: {
    // call api
    async tranferData() {
      if (await Authentication.tranferData()) {
        this.routeUserAfterLogin()
      } else {
        await this.$store.dispatch('logout')
      }
    },

    async loadShadowUsers() {
      await this.$store.getters.getAppUserInstance().loadShadowUsers()
      return true
    },

    // do the normal login with username and password
    login() {
      if (this.$refs.loginForm.validate()) {
        /* global EventBus axios */
        EventBus.$emit('spinnerShow')

        shopInstance(false)
          .post('/authenticate', {
            mail: this.mail,
            password: this.password,
            longLivingToken: this.saveCookie,
          })
          .then((response) => {
            // store user in the store
            this.$store.commit('setUser', response.data)
            this.$store.commit(
              'storeAuthorization',
              response.headers.authorization
            )

            //store authorization and uid as cookies
            const domain = Authentication.getDomain()
            const profileId = response.data.profile.id
            const authorizationKey = response.headers.authorization
            cookies.set('uid', response.data.profile.id, {
              expires: 90,
              domain,
            })
            cookies.set('authorization', response.headers.authorization, {
              expires: 90,
              domain,
            })

            this.routeUserAfterLogin()
          })
          .catch((error) => {
            if (error.response.status === 403) {
              EventBus.$emit(
                'notify',
                this.$root.$t('loginForm.authenticationFailed'),
                'error'
              )
            } else {
              EventBus.$emit(
                'notify',
                this.$root.$t('notify.loginError'),
                'error'
              )
            }
          })
          .finally(() => {
            EventBus.$emit('spinnerHide')
          })
      }
    },

    // check, if the login can be done via cookie
    performAutoLogin() {
      const authorizationKey = cookies.get('authorization')
      const uid = cookies.get('uid')
      if (authorizationKey && uid) {
        /* global EventBus axios */
        EventBus.$emit('spinnerShow', this.$t('spinner.tryToLogin'))

        // get authorization cookie and store it to vuex store
        this.$store.commit('storeAuthorization', authorizationKey)

        // load actual user from api
        shopInstance(false)
          .get('/user')
          .then((response) => {
            // save user in store
            this.$store.commit('setUser', response.data)
            this.routeUserAfterLogin()
          })
          .catch(() => {
            // delete cookies, if there are any
            const domain = Authentication.getDomain()
            const subdomain = Authentication.getSubDomain()
            cookies.remove('authorization', { domain })
            cookies.remove('uid', { domain })
            // fix: T1-2655
            cookies.remove('authorization', { domain: subdomain })
            cookies.remove('uid', { domain: subdomain })

            // logging user out (changing header items)
            this.$store.state.authorization = 0
          })
          .finally(() => {
            EventBus.$emit('spinnerHide')
          })
      }
    },

    // make routing depending on the user level
    async routeUserAfterLogin() {
      // notify user about login success
      EventBus.$emit('notify', this.$root.$t('notify.loginSuccess'), 'success')

      // emit login success
      this.$emit('LoginForm:loginSuccess')

      // only perform routing, if not deactivated. Used in booking process
      if (!this.noRouting) {
        // normal redirection
        const backendRoute = await this.$store.getters
          .getRouterHelperInstance()
          .getFirstBackendRouteWhereUserHasPermissionFor()

        // load all shadow profiles. but only if not in default destination to save unnecessary data
        await this.loadShadowUsers()

        // did we find any backend route?
        if (backendRoute) {
          // notify user about login success
          EventBus.$emit(
            'notify',
            this.$root.$t('notify.loginSuccess'),
            'success'
          )
          await this.$router.push(backendRoute)
        }
        // no backend route found for the user
        else {
          // differentiate between default destination and normal destination
          await this.$store.dispatch('logout', {
            redirectAfterLogout: 'noAccess',
            notify: false,
          })
        }
      } else {
        // also load shadow profiles after no routing
        // load all shadow profiles
        await this.loadShadowUsers()
      }
    },
  },
}
</script>
